/**
 * React Query hooks for multi-section timetable operations
 * 
 * Task 2.2: Create React Query hooks for new endpoints
 * Requirements: 7.1
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { 
  MultiSectionTimetableRequest,
  MultiSectionTimetableResponse,
  TeacherSuggestion,
  TeacherSuggestionsResponse,
  SchoolRulesResponse,
  UpdateSchoolRulesRequest,
  AutoFillRequest,
  AutoFillResponse,
} from '@timetable/types';
import {
  getMultiSectionTimetable,
  getTeacherSuggestions,
  getSchoolRules,
  updateSchoolRules,
  getTeacherClassAssociations,
  createTeacherClassAssociation,
  autoFillTimetable,
} from '@timetable/api';
import { queryKeys } from '../lib/queryKeys';

/**
 * Hook to fetch multi-section timetable data
 * 
 * Fetches all sections and timetable records for a class
 * Requirements: 1.1, 2.2, 7.1
 */
export function useMultiSectionTimetable(
  classId: string | null,
  academicYear: string | null,
  date?: string | null,
  options?: { enabled?: boolean; academicYearId?: string | null }
) {
  return useQuery<MultiSectionTimetableResponse>({
    queryKey: queryKeys.timetable.multiSection(
      classId || '',
      academicYear || '',
      date || undefined
    ),
    queryFn: () => {
      if (!classId || !academicYear) {
        throw new Error('classId and academicYear are required');
      }
      return getMultiSectionTimetable({
        classId,
        academicYear,
        date: date ?? undefined,
        academicYearId: options?.academicYearId ?? '',
      });
    },
    enabled: options?.enabled !== false && !!classId && !!academicYear,
  });
}

/**
 * Hook to fetch teacher suggestions for a period slot
 * 
 * Returns ranked teacher suggestions based on intelligent scoring
 * Requirements: 15.2, 15.3, 15.4, 15.5, 15.6, 15.7, 15.8, 15.9, 15.10
 */
export function useTeacherSuggestions(
  sectionId: string | null,
  periodId: string | null,
  day: string | null,
  subjectId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery<TeacherSuggestionsResponse>({
    queryKey: queryKeys.timetable.teacherSuggestions(
      sectionId || '',
      periodId || '',
      day || '',
      subjectId || ''
    ),
    queryFn: () => {
      if (!sectionId || !periodId || !day || !subjectId) {
        throw new Error('All parameters are required for teacher suggestions');
      }
      return getTeacherSuggestions({
        sectionId,
        periodId,
        day: day as 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday',
        subjectId,
      });
    },
    enabled:
      options?.enabled !== false &&
      !!sectionId &&
      !!periodId &&
      !!day &&
      !!subjectId,
  });
}

/**
 * Hook to fetch school rules configuration
 * 
 * Returns school-specific rules and constraints
 * Requirements: 18.1, 18.9, 18.11
 */
export function useSchoolRules(
  schoolId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery<SchoolRulesResponse>({
    queryKey: queryKeys.schools.rules(schoolId || ''),
    queryFn: () => {
      if (!schoolId) throw new Error('schoolId is required');
      return getSchoolRules({ schoolId, academicYear: '' });
    },
    enabled: options?.enabled !== false && !!schoolId,
  });
}

/**
 * Hook to fetch teacher class associations
 * 
 * Returns classes assigned to a teacher for a specific academic year
 * Requirements: 18.2
 */
export function useTeacherAssociations(
  teacherId: string | null,
  academicYear?: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.teachers.associations(
      teacherId || '',
      academicYear || undefined
    ),
    queryFn: () => {
      if (!teacherId) throw new Error('teacherId is required');
      return getTeacherClassAssociations({ teacherId, ...(academicYear ? { academicYear } : {}) });
    },
    enabled: options?.enabled !== false && !!teacherId,
  });
}

/**
 * Mutation hook for auto-fill operation
 * 
 * Automatically assigns teachers to empty slots based on intelligent suggestions
 * Requirements: 15.15, 15.16
 */
export function useAutoFill() {
  const queryClient = useQueryClient();

  return useMutation<AutoFillResponse, Error, AutoFillRequest>({
    mutationFn: (request: AutoFillRequest) => autoFillTimetable(request),
    onSuccess: (data, variables) => {
      // Invalidate multi-section timetable query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.timetable.multiSection(
          variables.classId,
          variables.academicYear,
          undefined
        ),
      });
      // Also invalidate the broader timetable list for cross-class conflict detection
      queryClient.invalidateQueries({ queryKey: queryKeys.timetable.all });
    },
  });
}

/**
 * Mutation hook for updating school rules
 * 
 * Updates school-specific rules and constraints
 * Requirements: 18.1, 18.9, 18.11
 */
export function useUpdateSchoolRules(schoolId: string) {
  const queryClient = useQueryClient();

  return useMutation<SchoolRulesResponse, Error, UpdateSchoolRulesRequest>({
    mutationFn: (rules: UpdateSchoolRulesRequest) => updateSchoolRules(schoolId, '', rules),
    onSuccess: () => {
      // Invalidate school rules query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.schools.rules(schoolId),
      });
    },
  });
}

/**
 * Mutation hook for creating/updating teacher class associations
 * 
 * Assigns teachers to classes they can teach
 * Requirements: 18.2
 */
export function useUpdateTeacherAssociations(teacherId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    Error,
    { classIds: string[]; academicYear: string }
  >({
    mutationFn: (data: { classIds: string[]; academicYear: string }) =>
      Promise.all(
        data.classIds.map(classId =>
          createTeacherClassAssociation({ teacher_id: teacherId, class_id: classId, academic_year: data.academicYear })
        )
      ),
    onSuccess: (_, variables) => {
      // Invalidate teacher associations query to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.teachers.associations(
          teacherId,
          variables.academicYear
        ),
      });
    },
  });
}
